import React from 'react';
import { TouchableOpacity, Text, StyleSheet, ViewStyle, TextStyle } from 'react-native';
import { LucideIcon } from 'lucide-react-native';
import { useCurrentTheme } from '@/theming/useCurrentTheme';
import { ThemeType } from '@/theming/baseTheme';

interface ActionButtonProps {
    icon: LucideIcon;
    text: string;
    onPress: () => void;
    isActive?: boolean;
    activeIcon?: LucideIcon;
    activeText?: string;
    style?: ViewStyle;
    textStyle?: TextStyle;
    disabled?: boolean;
    size?: 'small' | 'medium' | 'large';
}

export const ActionButton: React.FC<ActionButtonProps> = ({
                                                              icon: Icon,
                                                              text,
                                                              onPress,
                                                              isActive = false,
                                                              activeIcon: ActiveIcon,
                                                              activeText,
                                                              style,
                                                              textStyle,
                                                              disabled = false,
                                                              size = 'medium',
                                                          }) => {
    const { theme } = useCurrentTheme();
    const styles = createStyles(theme, isActive, size);

    const getIconSize = () => {
        switch (size) {
            case 'small': return 16;
            case 'large': return 24;
            default: return 20;
        }
    };

    const CurrentIcon = isActive && ActiveIcon ? ActiveIcon : Icon;
    const currentText = isActive && activeText ? activeText : text;

    return (
        <TouchableOpacity
            style={[styles.button, style]}
            onPress={onPress}
            activeOpacity={0.7}
            disabled={disabled}
        >
            <CurrentIcon
                size={getIconSize()}
                color={theme.colors.secondary}
                strokeWidth={2}
                fill={isActive && !ActiveIcon ? theme.colors.secondary : 'transparent'}
            />
            <Text style={[styles.text, textStyle]}>
                {currentText}
            </Text>
        </TouchableOpacity>
    );
};

const createStyles = (theme: ThemeType, isActive: boolean, size: string) => StyleSheet.create({
    button: {
        flex: 1,
        backgroundColor: isActive
            ? `${theme.colors.success}15`
            : `${theme.colors.secondary}10`,
        borderWidth: 1,
        borderColor: isActive
            ? `${theme.colors.success}40`
            : `${theme.colors.secondary}30`,
        borderRadius: theme.borderRadius.md,
        padding: size === 'small' ? theme.spacing.sm : size === 'large' ? theme.spacing.lg : theme.spacing.md,
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'row',
        gap: 4,
    },
    text: {
        color: theme.colors.secondary,
        fontSize: size === 'small' ? theme.fontSize.sm : size === 'large' ? theme.fontSize.lg : theme.fontSize.md,
        fontWeight: '600',
    },
});
